import { PreRenderService } from './pre-render.service';
import {Inject, Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Observer, of, ReplaySubject } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { NgMapApiLoader } from '@ngui/map';
import LatLngLiteral = google.maps.LatLngLiteral;
import LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral;
import { UserService } from './user.service';
import { HasSubscriptions } from '../components/has-subscriptions';
import { APP_CONFIG } from '../app-config.constants';
import { IAppConfig } from '../app-config.interface';
import { ApiConfigService } from './api-config/api-config.service';
import { CacheService } from './cache.service';
import { BasicGeocodeResult } from '../classes/geocode';
import { ObjectResponse } from '../classes/object-response';

interface PointLiteral { x: number, y: number }

@Injectable()
export class GoogleMapsService extends HasSubscriptions {

  private readonly _mapsTileSize = 256; // the tile size of a square on Google maps
  private readonly mapSearchDimension = 1000; // default px size offset used for calculating bounds from a zoom level
  private _geocode: google.maps.Geocoder;
  private _autoComplete: google.maps.places.AutocompleteService;
  private _isServerOrBot: boolean;

  // Use this to ensure that anyone using this service has the google API loaded in correctly before usage
  hasLoaded: ReplaySubject<void> = new ReplaySubject();

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig,
    private apiConfigService: ApiConfigService,
    private loader: NgMapApiLoader,
    private userService: UserService,
    private prerenderService: PreRenderService,
    private http: HttpClient,
    private cache: CacheService
  ) {
    super();
    this._isServerOrBot = this.prerenderService.isServerMode || this.userService.isGoogleBot();
  }

  loadApi() {
    // loads the maps API if it isn't already
    if (!this._isServerOrBot && !this._geocode) {
      this.loader.load();
      this.addSub(this.loader.api$.subscribe(() => {
        this._geocode = new google.maps.Geocoder();
        this._autoComplete = new google.maps.places.AutocompleteService();
        this.hasLoaded.next(null);
    }));
    }
  }

  /**
   * Retrieve Google Location information based on a LatLng
   * @param latLng
   */
  performReverseGeocode(latLng: LatLngLiteral): Promise<google.maps.GeocoderResult[]> {
    if (this._isServerOrBot) return of(null).toPromise();
    this.loadApi();
    return Observable.create((observer: Observer<google.maps.GeocoderResult[]>) => {
      this.addSub(this.hasLoaded.pipe(take(1)).subscribe(() => {
        this._geocode.geocode({ location: latLng }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) observer.next(results);
            else {
              console.error(results, status);
              observer.next(null);
            }
            observer.complete();
          })
      }));
    }).toPromise();
  }

  /**
   * Retrieve Google Location information based on an address
   * @param address
   */
  performGeocode(address: string): Promise<google.maps.GeocoderResult[]> {
    if (this._isServerOrBot) return of(null).toPromise();
    this.loadApi();
    return Observable.create((observer: Observer<google.maps.GeocoderResult[]>) => {
      this.addSub(this.hasLoaded.pipe(take(1)).subscribe(() => {
        this._geocode.geocode(
          {
            address: address,
            componentRestrictions: {
              country: 'ca'
            }
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) observer.next(results);
            else {
              console.error(results, status);
              observer.next(null);
            }
            observer.complete();
          })
      }));
    }).toPromise();
  }

  performBasicGeocode(address: string): Promise<BasicGeocodeResult> {
    const base = this.apiConfigService.getServerRoot(this.appConfig.urls.geo.geocode);
    const route = this.appConfig.urls.geo.geocode;

    const params = new HttpParams().append('address', address);

    const hashKey = this.cache.getRequestHash(route, params);
    const hashValue = this.cache.get<ObjectResponse<BasicGeocodeResult>>(hashKey);
    if (hashValue) {
      return new Promise<BasicGeocodeResult>((resolve) => resolve(hashValue.result));
    }

    return this.http.get<ObjectResponse<BasicGeocodeResult>>(base + route, {params: params, withCredentials: true}).pipe(
      tap(res => this.cache.set(hashKey, res, 300)),
      map(res => res.result),
      catchError(_ => of(null))
    ).toPromise();
  }

  getPlacePredictions(searchInput: string, location: LatLngLiteral, radius: number, types?: string[] ): Observable<google.maps.places.AutocompletePrediction[]> {
    if (this._isServerOrBot) return of(null);
    this.loadApi();
    return Observable.create((observer: Observer<google.maps.places.AutocompletePrediction[]>) => {
      this.addSub(this.hasLoaded.pipe(take(1)).subscribe(() => {
        this._autoComplete.getPlacePredictions({
          input: searchInput,
          componentRestrictions: {country: 'ca'},
          location: new google.maps.LatLng(location.lat, location.lng),
          types: types,
          radius: radius
        }, (list, status) => {
          if (!list || status !== google.maps.places.PlacesServiceStatus.OK) {
            observer.next(null);
          } else {
            observer.next(list);
          }
          observer.complete();
        });
      }));
    });
  }

  public getLocationTypeFromZoomLevel(zoomLevel: number, results: google.maps.GeocoderResult[]): google.maps.GeocoderResult {
    // TODO: deal with no matching results
    // TODO: fine tune this
    return results.find((r) => {
      const types = r.types;
      // if (zoomLevel >= 17 && (types.indexOf('street_address') >= 0 || types.indexOf('premise') >= 0)) return true;
      if ((zoomLevel >= 16) && (types.indexOf('establishment') >= 0 || types.indexOf('point_of_interest') >= 0)) return true;
      else if (zoomLevel >= 12 && types.indexOf('neighborhood') >= 0) return true;
      else if (zoomLevel >= 10 && types.indexOf('sublocality') >= 0) return true;
      else if (zoomLevel >= 9 && types.indexOf('locality') >= 0) return true;
      else if (types.indexOf('political') >= 0) return true;
      else return false;
    });
  }

  // Back-calculates the bounds for the map given a center point and zoom level
  // Converts to/from mercator projection to xy to avoid distortion on the map
  public getBoundsForLocation(
      latLng: LatLngLiteral,
      zoom: number,
      viewportWidth: number = this.mapSearchDimension,
      viewportHeight: number = this.mapSearchDimension
  ): LatLngBoundsLiteral {
    // Use the zoom level to calculate the x/y (2D plane) offset using the viewport size
    const zoomFactor = Math.pow(2, zoom) * 2;
    const widthOffset = viewportWidth / zoomFactor;
    const heightOffset = viewportHeight / zoomFactor;

    // Convert the center lat/lng to the mercator projection 2D plane x,y pair
    const latLngPoint = this.fromLatLngToPoint(latLng);

    // Get the bounds for the mercator point and calculated offsets
    return this.getBoundsFromOffset(latLngPoint, widthOffset, heightOffset);
  }

  // Converts a mercator x,y point and offset into LatLngBounds for the map viewport
  private getBoundsFromOffset(point: PointLiteral, widthOffset: number, heightOffset: number): LatLngBoundsLiteral {
    const swCoordinate = this.fromPointToLatLng({ x: point.x - widthOffset, y: point.y + heightOffset });
    const neCoordinate = this.fromPointToLatLng({ x: point.x + widthOffset, y: point.y - heightOffset });
    return { east: neCoordinate.lng, north: neCoordinate.lat, south: swCoordinate.lat, west: swCoordinate.lng };
  }

  // Below functions are derived from https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames which gives formulas for the mercator projection
  // Get the mercator projection x,y coordinates of a latlng point
  // Uses the Google Maps tile size
  private fromLatLngToPoint(latLng: LatLngLiteral): PointLiteral {
    const x = (latLng.lng + 180) / 360 * this._mapsTileSize;
    const y = ((1 - Math.log(Math.tan(latLng.lat * Math.PI / 180) + 1 / Math.cos(latLng.lat * Math.PI / 180)) / Math.PI) / 2) * this._mapsTileSize;
    return { x, y };
  }

  // Get the latlng coordinates of a mercator projection x,y point
  // Uses the Google Maps tile size
  private fromPointToLatLng(point: PointLiteral): LatLngLiteral {
    const lng = this.normalizedLongitude(point.x / this._mapsTileSize * 360 - 180);
    const lat = (180 / Math.PI * Math.atan(Math.sinh(Math.PI * (1 - 2 * point.y / this._mapsTileSize))));
    return { lat: lat, lng: lng };
  }

  // normalizes longitudes which have wrapped outside of the -180 to 180 range
  private normalizedLongitude(lng: number): number {
    while (Math.abs(lng) > 180) {
      lng -= Math.sign(lng) * 360;
    }
    return lng;
  }
}
